/**
 * useRentsoft — Hook für Rentsoft Integration (Mietwagen / MietGo)
 * 
 * Kommuniziert mit /api/rentsoft-sync Serverless Function,
 * die Rentsoft-Daten per Session-basiertem Scraping extrahiert.
 * 
 * Datenquellen:
 * - Fahrzeuge (10 aktive im Fuhrpark)
 * - Rechnungen (mit offenen Posten)
 * - Kunden
 * - Dashboard-KPIs
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// ─── Typen ──────────────────────────────────────────────

export interface RentsoftVehicle {
  rentsoft_id: string;
  name: string;
  marke: string;
  modell: string;
  kennzeichen: string;
  fin: string;
  km_stand: string;
  km_stand_num: number;
  kategorie: string;
  standort: string;
}

export interface RentsoftInvoice {
  rentsoft_id: string;
  rechnungs_nr: string;
  datum: string;
  typ: string;
  empfaenger: string;
  referenz: string;
  positionen: string;
  netto: string;
  netto_num: number;
  brutto: string;
  brutto_num: number;
}

export interface RentsoftOpenItem {
  rechnungs_nr: string;
  datum: string;
  typ: string;
  status: string;
  empfaenger: string;
  netto: string;
  netto_num: number;
}

export interface RentsoftCustomer {
  rentsoft_id: string;
  kunden_nr: string;
  name: string;
  typ: string;
  plz: string;
  ort: string;
  land: string;
}

export interface RentsoftCredentials {
  username: string;
  password: string;
}

interface RentsoftState {
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  vehicles: RentsoftVehicle[];
  invoices: RentsoftInvoice[];
  openItems: RentsoftOpenItem[];
  customers: RentsoftCustomer[];
  lastSync: string | null;
  fleetStats: {
    total: number;
    available: number;
    rented: number;
    maintenance: number;
  };
  financialStats: {
    openItemsCount: number;
    openItemsTotal: number;
    invoicesCount: number;
    monthlyRevenue: number;
  };
}

// ─── API-Aufruf ─────────────────────────────────────────

async function callRentsoftAPI(action: string, adminKey: string): Promise<any> {
  const response = await fetch(`/api/rentsoft-sync?action=${action}`, {
    headers: {
      'x-admin-key': adminKey,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || err.message || `API-Fehler: ${response.status}`);
  }

  return response.json();
}

// ─── Hook ───────────────────────────────────────────────

export function useRentsoft() {
  const [state, setState] = useState<RentsoftState>({
    isConnected: false,
    isLoading: false,
    error: null,
    vehicles: [],
    invoices: [],
    openItems: [],
    customers: [],
    lastSync: null,
    fleetStats: { total: 0, available: 0, rented: 0, maintenance: 0 },
    financialStats: { openItemsCount: 0, openItemsTotal: 0, invoicesCount: 0, monthlyRevenue: 0 },
  });

  const adminKeyRef = useRef<string>('');

  // Lade Admin-Key aus Supabase Session
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.access_token) {
        // Verwende den Supabase Service Role Key aus den Einstellungen
        // oder den Access Token als Fallback
        adminKeyRef.current = data.session.access_token;
      }
    });
  }, []);

  // ── Fahrzeuge laden ──
  const fetchVehicles = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callRentsoftAPI('vehicles', adminKeyRef.current);
      const vehicles = result.data || [];

      setState(s => ({
        ...s,
        isConnected: true,
        vehicles,
        fleetStats: {
          total: vehicles.length,
          available: vehicles.length, // Rentsoft zeigt keine Status-Info in der Liste
          rented: 0,
          maintenance: 0,
        },
        isLoading: false,
        lastSync: new Date().toISOString(),
      }));
      return vehicles;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return [];
    }
  }, []);

  // ── Rechnungen laden ──
  const fetchInvoices = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callRentsoftAPI('invoices', adminKeyRef.current);
      const invoices = result.data || [];
      setState(s => ({
        ...s,
        isConnected: true,
        invoices,
        isLoading: false,
      }));
      return invoices;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return [];
    }
  }, []);

  // ── Offene Posten laden ──
  const fetchOpenItems = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callRentsoftAPI('open-items', adminKeyRef.current);
      const openItems = result.data || [];
      const total = openItems.reduce((s: number, i: RentsoftOpenItem) => s + i.netto_num, 0);

      setState(s => ({
        ...s,
        isConnected: true,
        openItems,
        financialStats: {
          ...s.financialStats,
          openItemsCount: openItems.length,
          openItemsTotal: total,
        },
        isLoading: false,
      }));
      return openItems;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return [];
    }
  }, []);

  // ── Kunden laden ──
  const fetchCustomers = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callRentsoftAPI('customers', adminKeyRef.current);
      const customers = result.data || [];
      setState(s => ({
        ...s,
        isConnected: true,
        customers,
        isLoading: false,
      }));
      return customers;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return [];
    }
  }, []);

  // ── Vollsync (alle Daten + Supabase-Sync) ──
  const syncAll = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await callRentsoftAPI('sync-all', adminKeyRef.current);
      const data = result.data || {};

      setState(s => ({
        ...s,
        isConnected: true,
        vehicles: data.vehicles || [],
        invoices: data.invoices || [],
        openItems: data.open_items || [],
        fleetStats: {
          total: (data.vehicles || []).length,
          available: (data.vehicles || []).length,
          rented: 0,
          maintenance: 0,
        },
        financialStats: {
          openItemsCount: (data.open_items || []).length,
          openItemsTotal: (data.open_items || []).reduce((s: number, i: any) => s + (i.netto_num || 0), 0),
          invoicesCount: (data.invoices || []).length,
          monthlyRevenue: 0,
        },
        lastSync: data.synced_at || new Date().toISOString(),
        isLoading: false,
      }));

      return data;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return null;
    }
  }, []);

  // ── Login (Verbindung mit Credentials herstellen) ──
  const login = useCallback(async (_credentials: RentsoftCredentials): Promise<boolean> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      await syncAll();
      return true;
    } catch (err: any) {
      setState(s => ({ ...s, isLoading: false, error: err.message }));
      return false;
    }
  }, [syncAll]);

  // ── Disconnect / Reset ──
  const disconnect = useCallback(() => {
    setState({
      isConnected: false,
      isLoading: false,
      error: null,
      vehicles: [],
      invoices: [],
      openItems: [],
      customers: [],
      lastSync: null,
      fleetStats: { total: 0, available: 0, rented: 0, maintenance: 0 },
      financialStats: { openItemsCount: 0, openItemsTotal: 0, invoicesCount: 0, monthlyRevenue: 0 },
    });
  }, []);

  return {
    ...state,
    fetchVehicles,
    fetchInvoices,
    fetchOpenItems,
    fetchCustomers,
    syncAll,
    login,
    disconnect,
  };
}
